import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import MUIDataTable, { FilterType } from "mui-datatables";
import { useEffect, useState } from "react";
import { commonTableOptions } from "../../../constants";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import {
  handleError,
  parseAndFormateDateCredit,
  parseAndFormateDateCreditReview,
} from "../../../utils/common";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import useUserInfo from "../../../hooks/useUserInfo";
import { AuthenticatedUser } from "../../../interfaces/user.model";
import { instance } from "../../../utils/api";
import UpdateInvoiceModal from "./updateInvoiceModal";

const SupplierFinancing = () => {
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [updateInvoice, setUpdateInvoice] = useState(false);
  const [activeLocationId, setActiveLocationId] = useState<string>("");
  const [filterBy, setFilterBy] = useState("under_review");
  const user: AuthenticatedUser = useUserInfo();
  const [activeInvoiceUrl, setActiveInvoiceUrl] = useState<string>("");
  const [activeCreditUsageId, setActiveCreditUsageId] = useState<string>("");
  const [accountDetailsChecked, setAccountDetailsChecked] =
    useState<string>("");
  const [distributorName, setDistributorName] = useState<string>("");
  const [payableAmount, setPayableAmount] = useState<string>("");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [ifscCode, setIfscCode] = useState<string>("");

  useEffect(() => {
    const getData = async () => {
      if (data?.length > 0) {
        setData([]);
      }
      let payload = {
        metric_type: "credits_review",
        status: filterBy,
      };
      setLoading(true);
      try {
        const result = await instance.post(
          `${API_VERSION.V2}/analytics/digital-payments/overview`,
          payload,
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setData(result?.data?.response);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy]);

  const refreshUtr = async (creditUsageId: string) => {
    try {
      setLoading(true);

      const result = await instance.post(
        `${API_VERSION.V2}/payments/refresh-utr`,
        {
          credit_usage_id: creditUsageId,
        },
      );

      if (result?.data?.status === API_RESULT.SUCCESS) {
        setFilterBy((prev) => prev);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const cols = [
    {
      name: "location_id",
      label: "Location ID",
      options: {
        filterType: "textField" as FilterType,
        customBodyRender: (value: any) => {
          return (
            <Typography
              component="a"
              minWidth={160}
              href={`/merchants/${value}?tab=5`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
              }}
              sx={{
                color: "#0058FF",
                textDecoration: "underline",
                cursor: "pointer",
                whiteSpace: "nowrap",
                minWidth: 80,
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              variant="body2"
            >
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "supplier_name",
      label: "Distributor Name",
      options: {
        filterType: "textField" as FilterType,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={160} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "invoice_details",
      label: "Invoice Details",
      options: {
        searchable: false,
        sort: false,
        download: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const rowData = data[tableMeta.rowIndex];

          return (
            <Box>
              <Typography variant="body2">
                <strong>Invoice ID: </strong>
                {rowData.invoice_id}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> ₹
                {Number(rowData.invoice_amount || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                <strong>
                  Payable Amount: ₹
                  {Number(rowData.principal_amount || 0).toFixed(2)}
                </strong>
              </Typography>
              <Typography variant="body2">
                <strong>Invoice Date:</strong>{" "}
                {rowData.invoice_date && rowData.invoice_date !== "0000-00-00"
                  ? parseAndFormateDateCredit(rowData.invoice_date)
                  : "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Due Date:</strong>{" "}
                {rowData.invoice_due_date &&
                rowData.invoice_due_date !== "0000-00-00"
                  ? parseAndFormateDateCredit(rowData.invoice_due_date)
                  : "-"}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "invoice_id",
      label: "Invoice ID",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "invoice_amount",
      label: "Invoice Amount",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "principal_amount",
      label: "Payable Amount",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "invoice_date",
      label: "Invoice Date",
      options: {
        display: false,
        download: true,
        customBodyRender: (value: any) =>
          value && value !== "0000-00-00"
            ? parseAndFormateDateCredit(value)
            : "",
      },
    },
    {
      name: "invoice_due_date",
      label: "Invoice Due Date",
      options: {
        display: false,
        download: true,
        customBodyRender: (value: any) =>
          value && value !== "0000-00-00"
            ? parseAndFormateDateCredit(value)
            : "",
      },
    },
    {
      name: "credit_details",
      label: "Credit Details",
      options: {
        searchable: false,
        sort: false,
        download: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const rowData = data[tableMeta.rowIndex];

          return (
            <Box>
              <Typography variant="body2">
                <strong>Credit Usage ID: </strong>
                {rowData.credit_usage_id}
              </Typography>
              <Typography variant="body2">
                <strong>Principle Amount: </strong>₹{rowData.principal_amount}
              </Typography>
              <Typography variant="body2">
                <strong>Total Interest: </strong>₹{rowData.total_interest}
              </Typography>
              <Typography variant="body2">
                <strong>Total Payable: </strong>₹{rowData.total_payable}
              </Typography>
              <Typography variant="body2">
                <strong>EMI/day: </strong>₹{rowData.emi_per_day}
              </Typography>
              <Typography variant="body2">
                <strong>Tenure (in days): </strong>
                {rowData.tenure_days}
              </Typography>
              <Typography variant="body2">
                <strong>Interest Rate: </strong>
                {Number(rowData.interest_rate_per_day || 0).toFixed(2)}%
              </Typography>
              <Typography variant="body2">
                <strong>Total Interest:</strong> ₹
                {Number(rowData.total_interest || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                <strong>EMI/Day:</strong> ₹
                {Number(rowData.emi_per_day || 0).toFixed(2)}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "credit_usage_id",
      label: "Credit Usage ID",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "principal_amount",
      label: "Principle Amount",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "total_interest",
      label: "Total Interest",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "total_payable",
      label: "Total Payable",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "emi_per_day",
      label: "EMI/Day",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "tenure_days",
      label: "Tenure",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "interest_rate_per_day",
      label: "Interest Rate",
      options: {
        display: false,
        download: true,
        customBodyRender: (value: any) => `${Number(value || 0).toFixed(2)}%`,
      },
    },
    {
      name: "total_interest",
      label: "Total Interest",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "emi_per_day",
      label: "EMI/Day",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "",
      label: "Payment Status",
      options: {
        download: false,
        display: filterBy === "approved",
        customBodyRender: (_: any, tableMeta: any) => {
          const rowData = data[tableMeta.rowIndex];

          // const hasPaymentInfo =
          //   rowData.payment_mode || rowData.payment_utr || rowData.payment_at;

          return (
            <Box>
              <Typography variant="body2">
                <strong>Bank Account: </strong>
                {rowData.supplier_bank_account}
              </Typography>
              <Typography variant="body2">
                <strong>IFSC Code: </strong>
                {rowData.supplier_ifsc}
              </Typography>
              <Typography variant="body2">
                <strong>Payment Status: </strong>
                {rowData.payment_status}
              </Typography>
              <Typography variant="body2">
                <strong>Payment Mode: </strong>
                <span style={{ color: "#0058FF" }}>{rowData.payment_mode}</span>
              </Typography>
              <Typography variant="body2" component="span">
                <strong>Payment UTR: </strong>
                <span style={{ color: "#0058FF" }}>{rowData.payment_utr}</span>
              </Typography>
              <Typography variant="body2">
                <strong>Payment At: </strong>
                <span style={{ color: "#0058FF" }}>
                  {parseAndFormateDateCreditReview(rowData.payment_at)}
                </span>
              </Typography>
              {/* {!hasPaymentInfo ? (
                // 🔄 NO PAYMENT INFO STATE
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" component="span">
                    <strong>Payment Info: </strong>
                  </Typography>

                  <Tooltip title="Refresh to fetch payment information">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshUtr(rowData.credit_usage_id);
                      }}
                      sx={{
                        ml: 0.5,
                        border: "1px solid #E0E0E0",
                        borderRadius: "6px",
                        backgroundColor: "#F9FAFB",
                        "&:hover": {
                          backgroundColor: "#EEF2FF",
                        },
                      }}
                      disabled={loading}
                    >
                      <RefreshOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#0058FF",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <>
                  <Typography variant="body2">
                    <strong>Payment Mode: </strong>
                    {rowData.payment_mode}
                  </Typography>
                  <Typography variant="body2" component="span">
                    <strong>Payment UTR: </strong>
                    {rowData.payment_utr}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Payment At: </strong>
                    {rowData.payment_at}
                  </Typography>
                </>
              )} */}
            </Box>
          );
        },
      },
    },
    {
      name: "supplier_bank_account",
      label: "Bank Account",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "supplier_ifsc",
      label: "IFSC Code",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "payment_mode",
      label: "Payment Mode",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "payment_utr",
      label: "Payment UTR",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "payment_status",
      label: "Payment Status",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "payment_at",
      label: "Payment At",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "invoice_status",
      label: "Invoice Status",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "invoice_reject_reason",
      label: "Reject Reason",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "invoice_status",
      label: "Invoice Status",
      options: {
        searchable: false,
        customBodyRender: (value: any, tableMeta: any) => {
          // 👇 rowData is an ARRAY, aligned with columns
          const rowData = tableMeta.rowData;

          // Find index of invoice_reject_reason column
          const rejectReasonIndex = cols.findIndex(
            (c) => c.name === "invoice_reject_reason",
          );

          const rejectReason =
            rejectReasonIndex !== -1 ? rowData[rejectReasonIndex] : null;

          return (
            <>
              {value === "UNDER_REVIEW" && (
                <Chip
                  size="small"
                  label="UNDER REVIEW"
                  sx={{ backgroundColor: "#FBD38D", px: 1 }}
                />
              )}

              {value === "APPROVED" && (
                <Chip
                  size="small"
                  label="APPROVED"
                  sx={{ backgroundColor: "#C6F6D5", px: 1 }}
                />
              )}

              {value === "PENDING" && (
                <Chip
                  size="small"
                  label="PENDING"
                  sx={{ backgroundColor: "#c7c7c7", px: 1 }}
                />
              )}

              {value === "REJECTED" && (
                <Box>
                  <Chip
                    size="small"
                    label="REJECTED"
                    sx={{
                      backgroundColor: "#C53030",
                      color: "#FFF",
                      px: 1,
                    }}
                  />

                  {rejectReason && (
                    <Typography
                      variant="caption"
                      display="block"
                      mt={0.5}
                      color="text.secondary"
                    >
                      <strong>Reason:</strong> {rejectReason}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
    },
    {
      name: "",
      label: "Timestamp",
      options: {
        searchable: false,
        download: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const rowData = data[tableMeta.rowIndex];
          return (
            <Box>
              <Typography minWidth={120} variant="body2">
                <strong>Created At : </strong>
                {parseAndFormateDateCreditReview(rowData.created_at)}
              </Typography>
              <Typography minWidth={120} variant="body2">
                <strong>Updated At : </strong>
                {parseAndFormateDateCreditReview(rowData.updated_at)}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "created_at",
      label: "Created At",
      options: {
        searchable: false,
        display: false,
        download: true,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "updated_at",
      label: "Updated At",
      options: {
        searchable: false,
        display: false,
        download: true,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "invoice_url",
      label: "Invoice",
      options: {
        searchable: false,
        display: false,
        download: true,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "",
      label: "Actions",
      options: {
        download: false,
        display: filterBy === "under_review",
        customBodyRender: (value: any, tableMeta: any) => {
          return (
            <Box
              textAlign="center"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setActiveLocationId(data[tableMeta.rowIndex]?.location_id);
                setActiveInvoiceUrl(data[tableMeta.rowIndex]?.invoice_url);
                setAccountDetailsChecked(
                  data[tableMeta.rowIndex]?.account_details_checked,
                );
                setAccountNumber(
                  data[tableMeta.rowIndex]?.supplier_bank_account,
                );
                setIfscCode(data[tableMeta.rowIndex]?.supplier_ifsc);
                setActiveCreditUsageId(
                  data[tableMeta.rowIndex]?.credit_usage_id,
                );
                setDistributorName(data[tableMeta.rowIndex]?.supplier_name);
                setPayableAmount(
                  Number(
                    data[tableMeta.rowIndex]?.principal_amount || 0,
                  ).toFixed(2),
                );
                setUpdateInvoice(true);
              }}
            >
              <PermIdentityIcon />
              <Typography
                sx={{
                  fontSize: "10px !important",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Invoice
              </Typography>
            </Box>
          );
        },
      },
    },
  ];

  const CustomToolbar = () => {
    return (
      <FormControl size="small">
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          name="status"
          id="status"
          label="status"
          value={filterBy}
          onChange={(e) => setFilterBy(e.target.value)}
        >
          <MenuItem value="under_review">Under Review</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
        </Select>
      </FormControl>
    );
  };

  const options: any = {
    customToolbar: () => <CustomToolbar />,
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    searchPlaceholder: "Location ID/Distributor Name",
    filter: false,
    download: [
      "ravi@medpay.in",
      "arun@medpay.in",
      "tarun.yadav@medpay.in",
      "amit@medpay.in",
      "anuj@medpay.in",
      "onboardings@medpay.in",
      "joys.saji@medpay.in",
      "jai.srivastava@medpay.in",
      "anamika.shrivastava@medpay.in",
      "anirudh.agarwal@medpay.in",
      "sneha.jaiswal@medpay.in",
    ].includes(String(user?.user_id))
      ? true
      : false,
    downloadOptions: {
      filename:
        "Pending_Distributor_Invoice_" + new Date().toLocaleDateString(),
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      },
    },
  };

  return (
    <Box>
      <MUIDataTable title="" data={data} columns={cols} options={options} />
      {updateInvoice ? (
        <UpdateInvoiceModal
          updateInvoice={updateInvoice}
          setUpdateInvoice={setUpdateInvoice}
          activeLocationId={activeLocationId}
          invoiceUrl={activeInvoiceUrl}
          accountDetailsChecked={accountDetailsChecked}
          accountNumber={accountNumber}
          ifscCode={ifscCode}
          setAccountNumber={setAccountNumber}
          setIfscCode={setIfscCode}
          activeCreditUsageId={activeCreditUsageId}
          distributorName={distributorName}
          payableAmount={payableAmount}
          callBack={() =>
            setData(
              data?.filter(
                (item: any) => item.location_id !== activeLocationId,
              ),
            )
          }
        />
      ) : null}
    </Box>
  );
};

export default SupplierFinancing;
